import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useApp } from "@/context/AppContext";
import { GRANTS, TAX_CREDITS, SETTLEMENTS, formatMoney, formatDeadline, daysUntil } from "@/lib/mockData";
import MatchRing from "@/components/common/MatchRing";
import SignatureDialog from "@/components/common/SignatureDialog";
import { ArrowLeft, Calendar, Building2, CheckCircle2, FileText, Sparkles, Send, ShieldCheck, Download, ExternalLink, HelpCircle, Wand2, AlertCircle, Upload } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const toErr = (d, fallback = "Something went wrong") => {
    if (!d) return fallback;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
    if (typeof d === "object") return d.msg || d.detail || JSON.stringify(d);
    return String(d);
};

export default function Detail() {
    const { type, id } = useParams();
    const navigate = useNavigate();
    const {
        user, profile, applications, unclaimed, vaultDocs,
        startApplication, getApplication, prepareApplication, answerQuestion,
        signApplication, fileApplication, updateAppStatus, downloadPacket, deleteApplication, refreshApplications,
    } = useApp();

    const record = useMemo(() => lookupRecord(type, id, unclaimed), [type, id, unclaimed]);
    const existing = applications.find((a) => a.target_id === id);

    const [detailApp, setDetailApp] = useState(null); // full app with questions
    const [loadingPrep, setLoadingPrep] = useState(false);
    const [signing, setSigning] = useState(false);
    const [answers, setAnswers] = useState({});

    const refresh = async () => {
        if (!existing?.id) { setDetailApp(null); return; }
        const a = await getApplication(existing.id);
        setDetailApp(a);
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [existing?.id]);

    if (!record) {
        return (
            <div className="px-5 sm:px-8 py-20 max-w-3xl mx-auto text-center">
                <p className="text-muted-foreground">Program not found. It may have closed or moved.</p>
                <Button asChild className="mt-4 rounded-full"><Link to="/app/dashboard">Back to dashboard</Link></Button>
            </div>
        );
    }

    const title = record.title || record.name || record.description;
    const amount = record.amount || record.estimate || 0;
    const deadline = record.deadline || null;
    const days = daysUntil(deadline);
    const status = detailApp?.status || existing?.status;
    const progress = detailApp?.progress ?? existing?.progress ?? 0;
    const isSigned = status === "submitted" || status === "under_review" || status === "approved" || status === "denied" || status === "needs_more_info";
    const isFiled = ["under_review", "approved", "denied", "needs_more_info"].includes(status);
    const confirmation = detailApp?.confirmation_number || existing?.confirmation_number;
    const portalUrl = detailApp?.filed_portal_url || (record.portal_url || record.url);
    const questions = detailApp?.questions || [];
    const unanswered = questions.filter(q => !q.answered);

    const handleStart = async () => {
        try {
            setLoadingPrep(true);
            const created = await startApplication({
                type, target_id: id, target_title: title,
                target_amount: amount, target_meta: { org: record.org || record.agency || record.source, url: record.portal_url || record.url },
            });
            toast.success("Application opened", { description: "Owed is preparing the paperwork from your profile…" });
            // auto-trigger AI prepare
            const prepared = await prepareApplication(created.id);
            setDetailApp(prepared);
            if ((prepared.questions || []).filter(q => !q.answered).length === 0) {
                toast.success("Packet ready to e-sign", { description: "No additional info needed — it's all here." });
            } else {
                toast.message("Owed has a few questions", { description: `Answer ${prepared.questions.filter(q=>!q.answered).length} to finish preparing.` });
            }
        } catch (e) {
            toast.error(toErr(e?.response?.data?.detail, "Couldn't start the application."));
        } finally {
            setLoadingPrep(false);
        }
    };

    const handleRePrepare = async () => {
        if (!existing?.id) return;
        try {
            setLoadingPrep(true);
            const prepared = await prepareApplication(existing.id);
            setDetailApp(prepared);
            toast.success("Re-prepared with fresh AI pass");
        } catch (e) {
            toast.error(toErr(e?.response?.data?.detail, "Re-prepare failed"));
        } finally {
            setLoadingPrep(false);
        }
    };

    const saveAnswer = async (qid) => {
        const val = (answers[qid] || "").trim();
        if (!val) return toast.error("Please answer before saving");
        try {
            await answerQuestion(existing.id, qid, val);
            setAnswers(s => ({ ...s, [qid]: "" }));
            const a = await getApplication(existing.id);
            setDetailApp(a);
            toast.success("Saved");
        } catch (e) {
            toast.error(toErr(e?.response?.data?.detail, "Couldn't save answer"));
        }
    };

    const doSign = async (payload) => {
        await signApplication(existing.id, payload);
        setSigning(false);
        toast.success("Signed — your packet is ready to download.");
        const a = await getApplication(existing.id);
        setDetailApp(a);
    };

    const handleDownload = async () => {
        try {
            await downloadPacket(existing.id, `owed-${title.replace(/\s+/g, "-").toLowerCase().slice(0, 40)}.pdf`);
            toast.success("Packet downloaded");
        } catch (e) {
            toast.error("Download failed");
        }
    };

    const handleFile = async () => {
        if (!existing?.id) return;
        try {
            // Always download the packet first so the user has it on hand for the portal.
            await downloadPacket(existing.id, `owed-${title.replace(/\s+/g, "-").toLowerCase().slice(0, 40)}.pdf`);
            const res = await fileApplication(existing.id, {
                method: "portal",
                portal_url: portalUrl || null,
            });
            const refreshed = await getApplication(existing.id);
            setDetailApp(refreshed);
            toast.success(`Filed — confirmation ${res.confirmation_number}`, { description: "Packet downloaded. Open the agency portal to upload it." });
            // Open the agency portal for them
            if (portalUrl) window.open(portalUrl, "_blank", "noopener");
        } catch (e) {
            toast.error(toErr(e?.response?.data?.detail, "Couldn't file application"));
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (!existing?.id || !newStatus || newStatus === status) return;
        try {
            await updateAppStatus(existing.id, { status: newStatus });
            const refreshed = await getApplication(existing.id);
            setDetailApp(refreshed);
            toast.success("Status updated");
        } catch (e) {
            toast.error(toErr(e?.response?.data?.detail, "Couldn't update status"));
        }
    };

    const handleDelete = async () => {
        if (!existing?.id) return;
        if (!window.confirm("Delete this application? All draft progress will be lost.")) return;
        try {
            await deleteApplication(existing.id);
            toast.success("Application deleted");
            navigate("/app/applications");
        } catch { toast.error("Couldn't delete"); }
    };

    return (
        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-6xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="detail-back-btn">
                <ArrowLeft className="h-4 w-4" /> Back
            </button>

            {/* Hero */}
            <Card className="mt-5 overflow-hidden border-border">
                <div className="p-8 sm:p-10 flex flex-col md:flex-row gap-8 items-start">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="bg-secondary/60 border-border capitalize">{type}</Badge>
                            {record.category && <Badge variant="outline" className="bg-secondary/60 border-border capitalize">{record.category}</Badge>}
                            {record.difficulty && <Badge variant="outline" className={cn("text-xs", record.difficulty === "Easy" ? "bg-success/10 text-success border-success/30" : record.difficulty === "Medium" ? "bg-warning/10 text-warning border-warning/30" : "bg-terracotta/10 text-terracotta border-terracotta/30")}>{record.difficulty}</Badge>}
                            {record.simulated && <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">Simulated — verify on portal</Badge>}
                        </div>
                        <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-balance">{title}</h1>
                        <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">{record.description}</p>
                        <div className="mt-6 flex flex-wrap gap-6">
                            {(record.org || record.agency || record.source) && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Issuer</p>
                                    <p className="mt-1 text-sm font-medium flex items-center gap-1.5"><Building2 className="h-4 w-4 text-muted-foreground" /> {record.org || record.agency || record.source}</p>
                                </div>
                            )}
                            {deadline && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Deadline</p>
                                    <p className="mt-1 text-sm font-medium flex items-center gap-1.5"><Calendar className="h-4 w-4 text-muted-foreground" /> {formatDeadline(deadline)} {days !== null && <span className="text-muted-foreground">· {days}d</span>}</p>
                                </div>
                            )}
                            {(record.portal_url || record.url) && (
                                <a href={record.portal_url || record.url} target="_blank" rel="noreferrer" className="text-sm font-medium inline-flex items-center gap-1 hover:underline" data-testid="detail-portal-link">
                                    Official portal <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                            )}
                        </div>
                    </div>

                    <Card className="p-6 w-full md:w-80 shrink-0 bg-gradient-warm border-0">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Estimated value</p>
                        <p className="mt-2 font-serif text-5xl tracking-tight">{formatMoney(amount, { compact: amount >= 100000 })}</p>
                        {typeof record.matchScore === "number" && (
                            <div className="mt-4 flex items-center gap-3">
                                <MatchRing score={record.matchScore} size={48} />
                                <p className="text-sm text-muted-foreground leading-snug">Strong profile fit.</p>
                            </div>
                        )}
                        {!existing ? (
                            <Button onClick={handleStart} disabled={loadingPrep} className="mt-5 w-full rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="detail-start-application-btn">
                                {loadingPrep ? "Preparing…" : (<><Wand2 className="h-4 w-4 mr-1" /> Start & AI-prepare</>)}
                            </Button>
                        ) : isSigned ? (
                            <div className="mt-5 space-y-2">
                                {!isFiled ? (
                                    <>
                                        <div className="p-3 rounded-xl bg-success/10 text-success flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4" /> Signed — ready to file</div>
                                        <Button onClick={handleFile} className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="detail-file-btn"><Send className="h-4 w-4 mr-1" /> File application</Button>
                                        <Button onClick={handleDownload} variant="outline" className="w-full rounded-full bg-card" data-testid="detail-download-packet-btn"><Download className="h-4 w-4 mr-1" /> Download packet only</Button>
                                    </>
                                ) : (
                                    <>
                                        <div className="p-3 rounded-xl bg-success/10 text-success" data-testid="detail-filed-block">
                                            <div className="flex items-center gap-2 text-sm font-medium"><CheckCircle2 className="h-4 w-4" /> Filed</div>
                                            {confirmation && <p className="text-xs mt-1 font-mono opacity-90">Confirmation: {confirmation}</p>}
                                            <p className="text-xs mt-1 opacity-80 capitalize">Status: {(status || "").replace(/_/g, " ")}</p>
                                        </div>
                                        {portalUrl && (
                                            <a href={portalUrl} target="_blank" rel="noreferrer" className="block">
                                                <Button variant="outline" className="w-full rounded-full bg-card" data-testid="detail-open-portal-btn"><ExternalLink className="h-4 w-4 mr-1" /> Open agency portal</Button>
                                            </a>
                                        )}
                                        <Button onClick={handleDownload} variant="outline" className="w-full rounded-full bg-card" data-testid="detail-download-packet-btn"><Download className="h-4 w-4 mr-1" /> Download packet</Button>
                                        <select value={status} onChange={(e) => handleStatusChange(e.target.value)} className="w-full h-10 rounded-full border border-border bg-card text-sm px-4" data-testid="detail-status-select">
                                            <option value="under_review">Under review</option>
                                            <option value="needs_more_info">Agency requested more info</option>
                                            <option value="approved">Approved</option>
                                            <option value="denied">Denied</option>
                                        </select>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="mt-5 space-y-2">
                                <Progress value={progress} className="h-1.5" />
                                <p className="text-xs text-muted-foreground">{progress}% complete · {unanswered.length} open question{unanswered.length === 1 ? "" : "s"}</p>
                                {unanswered.length === 0 && (
                                    <Button onClick={() => setSigning(true)} className="mt-2 w-full rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="detail-esign-btn">
                                        <ShieldCheck className="h-4 w-4 mr-1" /> E-sign packet
                                    </Button>
                                )}
                            </div>
                        )}
                    </Card>
                </div>
            </Card>

            {/* Body */}
            <div className="mt-8 grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Questions */}
                    {existing && unanswered.length > 0 && (
                        <Card className="p-6 border-border" data-testid="detail-questions-card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="font-serif text-2xl flex items-center gap-2"><HelpCircle className="h-5 w-5 text-accent-foreground" /> Owed has {unanswered.length} question{unanswered.length === 1 ? "" : "s"}</h2>
                                    <p className="text-sm text-muted-foreground mt-1">Your profile didn't have these — answer once and we'll remember.</p>
                                </div>
                                <Button onClick={handleRePrepare} disabled={loadingPrep} size="sm" variant="ghost" className="rounded-full"><Sparkles className="h-3.5 w-3.5 mr-1" /> Re-prepare</Button>
                            </div>
                            <div className="mt-5 space-y-4">
                                {unanswered.map((q, i) => (
                                    <div key={q.id} className="p-4 rounded-xl bg-secondary/40 border border-border" data-testid={`detail-question-${i}`}>
                                        <p className="text-sm font-medium">{q.question}</p>
                                        {q.reason && <p className="text-xs text-muted-foreground mt-1 italic">Why we're asking: {q.reason}</p>}
                                        <Textarea
                                            value={answers[q.id] || ""}
                                            onChange={(e) => setAnswers(s => ({ ...s, [q.id]: e.target.value }))}
                                            placeholder="Your answer…"
                                            className="mt-3 min-h-[80px] rounded-xl bg-card"
                                            data-testid={`detail-answer-input-${i}`}
                                        />
                                        <div className="mt-2 flex justify-end">
                                            <Button size="sm" onClick={() => saveAnswer(q.id)} className="rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid={`detail-save-answer-${i}`}>Save answer</Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* AI-filled fields */}
                    {existing && detailApp?.filled_fields && Object.keys(detailApp.filled_fields).length > 0 && (
                        <Card className="p-6 border-border">
                            <h2 className="font-serif text-2xl flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> AI-prepared fields</h2>
                            <p className="text-sm text-muted-foreground mt-1">Pulled from your vault. Edit the vault to change these permanently.</p>
                            <div className="mt-4 grid sm:grid-cols-2 gap-2">
                                {Object.entries(detailApp.filled_fields).map(([k, v]) => (
                                    <div key={k} className="p-3 rounded-lg bg-secondary/40 border border-border">
                                        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</p>
                                        <p className="text-sm mt-0.5 break-words">{String(v ?? "—")}</p>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Narrative */}
                    {existing && detailApp?.narrative && (
                        <Card className="p-6 border-border">
                            <h2 className="font-serif text-2xl">AI narrative draft</h2>
                            <p className="text-sm text-muted-foreground mt-1">Review and edit in your packet after download.</p>
                            <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed font-serif text-foreground/90 p-4 rounded-xl bg-secondary/30 border border-border">{detailApp.narrative}</div>
                        </Card>
                    )}

                    {/* Eligibility checklist from catalog */}
                    {record.eligibility && (
                        <Card className="p-6 border-border">
                            <h2 className="font-serif text-2xl">Eligibility criteria</h2>
                            <p className="text-sm text-muted-foreground mt-1">What the issuer requires. Owed AI flags anything you're missing.</p>
                            <ul className="mt-5 space-y-2">
                                {record.eligibility.map((e, i) => (
                                    <li key={i} className="flex items-start gap-3 p-2">
                                        <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />
                                        <span className="text-sm leading-snug">{e}</span>
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    )}
                </div>

                <aside className="space-y-6">
                    <DocumentChecklist detailApp={detailApp} vaultDocs={vaultDocs} />

                    <Card className="p-6 border-border">
                        <h3 className="font-serif text-xl">How this works</h3>
                        <ol className="mt-4 space-y-3 text-sm">
                            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs inline-flex items-center justify-center shrink-0">1</span><span>AI prepares your packet from the vault.</span></li>
                            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs inline-flex items-center justify-center shrink-0">2</span><span>Answer any follow-up questions we can't infer.</span></li>
                            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs inline-flex items-center justify-center shrink-0">3</span><span>E-sign the packet — legally binding under E-SIGN Act.</span></li>
                            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs inline-flex items-center justify-center shrink-0">4</span><span>Download PDF and submit through the agency portal.</span></li>
                        </ol>
                        <Separator className="my-6" />
                        <div className="flex items-start gap-3">
                            <ShieldCheck className="h-4 w-4 text-success shrink-0 mt-0.5" />
                            <p className="text-xs text-muted-foreground leading-relaxed">Nothing leaves this device without your signature. Your vault stays encrypted with us.</p>
                        </div>
                    </Card>

                    {existing && !isSigned && (
                        <Card className="p-6 border-border">
                            <h3 className="font-serif text-xl">Manage draft</h3>
                            <p className="text-sm text-muted-foreground mt-1">Delete this draft if you want to start fresh.</p>
                            <Button onClick={handleDelete} variant="outline" className="mt-4 w-full rounded-full border-destructive/30 text-destructive hover:bg-destructive/5" data-testid="detail-delete-btn">
                                <FileText className="h-4 w-4 mr-1" /> Delete draft
                            </Button>
                        </Card>
                    )}
                </aside>
            </div>

            {existing && (
                <SignatureDialog
                    open={signing}
                    onOpenChange={setSigning}
                    onConfirm={doSign}
                    defaultName={`${profile.legal_first_name || ""} ${profile.legal_last_name || ""}`.trim() || user?.name || ""}
                />
            )}
        </div>
    );
}

function lookupRecord(type, id, unclaimedLive) {
    if (type === "grant") return GRANTS.find((x) => x.id === id);
    if (type === "claim") return (unclaimedLive || []).find((x) => x.id === id);
    if (type === "credit") return TAX_CREDITS.find((x) => x.id === id);
    if (type === "settlement") return SETTLEMENTS.find((x) => x.id === id);
    return null;
}


/**
 * AI-flagged per-application document checklist + fallback packet-attachments list.
 * When `detailApp.required_documents` is populated by the AI prepare step, we
 * show each required/recommended vault kind with a live ✓/⚠ indicator. If the
 * AI hasn't run yet, we fall back to showing the vault's current contents.
 */
function DocumentChecklist({ detailApp, vaultDocs }) {
    const required = detailApp?.required_documents || [];
    const haveKinds = new Set((vaultDocs || []).map(d => d.kind));

    if (required.length > 0) {
        const missingCount = required.filter(r => !haveKinds.has(r.kind)).length;
        return (
            <Card className="p-6 border-border" data-testid="detail-checklist-card">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-serif text-xl">Document checklist</h3>
                        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1 inline-flex items-center gap-1.5">
                            <Sparkles className="h-3 w-3" /> AI flagged for this program
                        </p>
                    </div>
                    {missingCount > 0 ? (
                        <Badge variant="outline" className="bg-warning/10 border-warning/40 text-warning text-[11px] shrink-0" data-testid="detail-checklist-missing-badge">
                            {missingCount} missing
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="bg-success/10 border-success/40 text-success text-[11px] shrink-0" data-testid="detail-checklist-complete-badge">
                            Complete
                        </Badge>
                    )}
                </div>
                <ul className="mt-4 space-y-3">
                    {required.map((r, i) => {
                        const have = haveKinds.has(r.kind);
                        const doc = (vaultDocs || []).find(d => d.kind === r.kind);
                        return (
                            <li key={`${r.kind}-${i}`} className="flex items-start gap-3" data-testid={`detail-checklist-row-${r.kind}`}>
                                {have ? (
                                    <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />
                                ) : (
                                    <AlertCircle className={cn("h-4 w-4 shrink-0 mt-0.5", r.priority === "required" ? "text-warning" : "text-muted-foreground")} />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className={cn("text-sm font-medium leading-tight", !have && "text-foreground")}>
                                            {doc?.kind_label || kindLabel(r.kind)}
                                        </p>
                                        {r.priority === "required" && (
                                            <span className="text-[10px] uppercase tracking-wider font-mono text-warning">required</span>
                                        )}
                                    </div>
                                    {r.reason && <p className="text-xs text-muted-foreground leading-snug mt-0.5">{r.reason}</p>}
                                </div>
                            </li>
                        );
                    })}
                </ul>
                {missingCount > 0 && (
                    <Button asChild variant="outline" className="mt-5 w-full rounded-full" data-testid="detail-checklist-upload-btn">
                        <Link to="/app/vault"><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload missing documents</Link>
                    </Button>
                )}
                {missingCount === 0 && (
                    <p className="mt-5 text-xs text-muted-foreground leading-relaxed">
                        Everything on the issuer's list is in your vault — Owed will auto-attach it to your signed packet.
                    </p>
                )}
            </Card>
        );
    }

    // Fallback: show current vault contents (pre-prepare state).
    return (
        <Card className="p-6 border-border" data-testid="detail-attachments-card">
            <h3 className="font-serif text-xl">Packet attachments</h3>
            {(vaultDocs?.length || 0) === 0 ? (
                <>
                    <p className="text-sm text-muted-foreground mt-1">No supporting documents in your vault yet. Run AI prep below — Owed will tell you exactly which docs this program requires.</p>
                    <Button asChild variant="outline" className="mt-4 w-full rounded-full" data-testid="detail-goto-vault-btn">
                        <Link to="/app/vault">Upload ID / proof of address</Link>
                    </Button>
                </>
            ) : (
                <>
                    <p className="text-sm text-muted-foreground mt-1">{vaultDocs.length} document{vaultDocs.length === 1 ? "" : "s"} will be auto-attached as an appendix:</p>
                    <ul className="mt-3 space-y-1.5 text-sm">
                        {vaultDocs.slice(0, 4).map(d => (
                            <li key={d.id} className="flex items-center gap-2 text-muted-foreground">
                                <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                                <span className="truncate">{d.kind_label || d.kind}</span>
                            </li>
                        ))}
                        {vaultDocs.length > 4 && <li className="text-xs text-muted-foreground">+{vaultDocs.length - 4} more</li>}
                    </ul>
                    <Button asChild variant="ghost" size="sm" className="mt-3 rounded-full w-full">
                        <Link to="/app/vault">Manage documents →</Link>
                    </Button>
                </>
            )}
        </Card>
    );
}

const VAULT_KIND_LABELS = {
    government_id: "Government-issued photo ID",
    proof_of_address: "Proof of address",
    ssn_card: "Social Security card",
    tax_return: "Recent tax return",
    w2: "W-2 / 1099",
    bank_statement: "Bank statement",
    business_doc: "Business formation / EIN letter",
    birth_certificate: "Birth certificate",
    affidavit: "Sworn affidavit",
    other: "Other supporting document",
};
function kindLabel(kind) {
    return VAULT_KIND_LABELS[kind] || kind;
}
